import { IProductResult, productResultGetters } from '@unified-commerce/gpc-vue-storefront-search';
import { Product, productGetters } from '@unified-commerce/gpc-vue-storefront-shopify';
import qs from 'qs';

import { IProductCardProduct } from './types';

const getProductLink = (
  product: IProductResult,
  hideFitmentInUrl?: boolean,
  queryId?: string,
  position?: number,
) => {
  const params = qs.stringify({
    sid: queryId,
    pos: position,
    ...(hideFitmentInUrl ? { hide_fitment: true } : {}),
  });
  return `/products/${productResultGetters.getSlug(product)}${params ? `?${params}` : ''}`;
};

interface ProductResultGettersToProductCardParams {
  product: IProductResult;
  queryId?: string;
  position?: number;
  brandLink?: string;
  vehicleId?: number;
  hideFitmentInUrl?: boolean;
}

export const productResultGettersToProductCard = ({
  product,
  queryId,
  position,
  brandLink,
  vehicleId,
  hideFitmentInUrl,
}: ProductResultGettersToProductCardParams): IProductCardProduct => {
  return {
    id: productResultGetters.getId(product),
    name: productResultGetters.getName(product),
    price: productResultGetters.getPrice(product) ?? 0,
    compareToPrice: productResultGetters.getCompareToPrice(product) ?? undefined,
    maxPrice:
      // typed as "true" | "false" but Algolia returns a boolean
      String(product.record?.requires_shipping) === 'false'
        ? product.record.variants_max_price ?? undefined
        : undefined,
    availability: productResultGetters.getStockStatus(product),
    quantity: productResultGetters.getQuantity(product),
    partNumber: productResultGetters.getProductPartNumber(product),
    vendor: productResultGetters.getProductVendor(product),
    sku: productResultGetters.getProductSKU(product),
    tags: productResultGetters.getTags(product) || [],
    brand: productResultGetters.getProductVendor(product),
    isInStock: productResultGetters.isInStock(product),
    isSpecialOrder: productResultGetters.isSpecialOrder(product),
    isBackOrder: productResultGetters.isBackorder(product),
    isInStoreOnly: productResultGetters.isInStoreOnly(product),
    brandLink: brandLink,
    savingsTag: productResultGetters.getSavingsLabel(product),
    productImage: productResultGetters.getCoverImage(product),
    productImageAlt: productResultGetters.getName(product)!,
    productImageTag: undefined,
    link: getProductLink(product, hideFitmentInUrl, queryId, position),
    variantId: productResultGetters.getBase64ProductVariantId(product),
    hasVariants: productResultGetters.hasVariants(product),
    objectId: productResultGetters.getObjectID(product),
    queryId,
    position,
    merchandiseId: productResultGetters.getProductVariantId(product),
    fitsVehicle: productResultGetters.fitsVehicle(product, vehicleId),
    hasFitment: productResultGetters.hasFitment(product),
    isUniversalFitment: productResultGetters.isUniversal(product),
  };
};

export const productResultToProductCard = ({
  product,
  brandLink,
}: {
  product: Product;
  brandLink?: string;
}): IProductCardProduct => {
  return {
    id: productGetters.getId(product),
    name: productGetters.getName(product),
    price: productGetters.getPrice(product) ?? 0,
    compareToPrice: productGetters.getCompareToPrice(product) ?? undefined,
    availability: productGetters.getStockStatus(product),
    quantity: productGetters.getBundledQuantity(product),
    partNumber: productGetters.getProductVariantMetafield(product, 'partNumber'),
    vendor: productGetters.getProductVendor(product),
    sku: productGetters.getSKU(product),
    tags: productGetters.getTags(product) || [],
    brand: productGetters.getVendorName(product),
    isInStock: productGetters.isInStock(product),
    isSpecialOrder: productGetters.isSpecialOrder(product),
    isBackOrder: productGetters.isBackorder(product),
    isInStoreOnly: productGetters.isProductInStoreOnly(product),
    brandLink: brandLink,
    savingsTag: productGetters.getSavingsLabel(product),
    productImage: productGetters.getCoverImage(product),
    productImageAlt: productGetters.getName(product),
    productImageTag: undefined,
    link: `/products/${productGetters.getSlug(product)}`,
    variantId: productGetters.getVariantId(product),
    hasVariants: productGetters.hasVariants(product),
    objectId: undefined,
    queryId: undefined,
    position: undefined,
    merchandiseId: productGetters.getVariantId(product),
    hasFitment: productGetters.hasFitment(product),
    fitsVehicle: undefined, // todo - is there a suitable getter?
    isUniversalFitment: productGetters.isUniversal(product),
  };
};
